import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen, Share2, Mail, BarChart3, Copy, ExternalLink, RefreshCw,
  Loader2, Check, ArrowLeft, Sparkles, Linkedin, Twitter, MessageCircle, Facebook, AlignLeft,
  FileText, Clock, TrendingUp, Globe, Calendar,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import { adminApi } from "@/lib/adminApi";
import { useT } from "@/i18n/LanguageContext";

/**
 * /admin/publishing-studio — The publishing headquarters of StoicOs.ai.
 *
 * Philosophy: create once, publish everywhere, bring people back home.
 *
 * Four tabs:
 *   · Journal    — grid of published articles + Generate Social Content
 *   · Social     — per-article, per-platform copy studio (LinkedIn/X/Threads/Substack)
 *   · Newsletter — weekly digest generator (Subject/Intro/Main/Closing/CTA)
 *   · Analytics  — real DB counters + placeholder tiles for GA/Clarity
 */

const TABS = [
  { id: "journal",    labelKey: "pubstudio.tab.journal",    icon: BookOpen },
  { id: "social",     labelKey: "pubstudio.tab.social",     icon: Share2 },
  { id: "newsletter", labelKey: "pubstudio.tab.newsletter", icon: Mail },
  { id: "analytics",  labelKey: "pubstudio.tab.analytics",  icon: BarChart3 },
];

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin,       accent: "text-[#5B93FF]" },
  { id: "x",        label: "X",        icon: Twitter,        accent: "text-bone" },
  { id: "threads",  label: "Threads",  icon: MessageCircle,  accent: "text-bone" },
  { id: "facebook", label: "Facebook", icon: Facebook,       accent: "text-[#4599FF]" },
  { id: "substack", label: "Substack", icon: FileText,       accent: "text-[#FF6719]" },
  { id: "summary",  label: "Summary",  icon: AlignLeft,      accent: "text-bone" },
];

// ------------------------- helpers -------------------------

function pickTitle(article, lang = "en") {
  const t = article?.title || {};
  return t[lang] || t.en || t.nl || article?.slug || "Untitled";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ------------------------- shared primitives -------------------------

function TabButton({ tab, active, onClick, t }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pubstudio-tab-${tab.id}`}
      className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors border-b-2 ${
        active
          ? "text-gold border-gold"
          : "text-bone/55 border-transparent hover:text-bone hover:border-bone/20"
      }`}
    >
      <Icon size={14} strokeWidth={1.5} />
      {t(tab.labelKey)}
    </button>
  );
}

function CopyButton({ text, size = "md", testId, onCopied }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    const ok = await copyToClipboard(text || "");
    if (ok) {
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 1600);
    }
  };
  const cls = size === "sm"
    ? "inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-2.5 py-1.5 border border-gold/30 text-bone/80 hover:text-gold hover:border-gold/60 transition-colors"
    : "inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase px-4 py-2.5 border border-gold/40 text-gold hover:bg-gold/10 transition-colors";
  return (
    <button type="button" onClick={handle} data-testid={testId} className={cls} disabled={!text}>
      {copied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={1.5} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ------------------------- Journal tab -------------------------

function ArticleCard({ article, onOpen, t }) {
  const title = pickTitle(article, article.primary_language || "en");
  const lang = (article.primary_language || "en").toUpperCase();
  const counts = article.generated_counts || {};
  const totalGenerated = Object.values(counts).reduce((a, b) => a + b, 0);
  const cover = article.cover;
  return (
    <article
      data-testid={`pubstudio-article-${article.slug}`}
      className="group flex flex-col bg-[#0A0908] border border-white/8 hover:border-gold/40 transition-colors"
    >
      <div className="aspect-[16/9] bg-black overflow-hidden relative">
        {cover ? (
          <img
            src={cover}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-bone/20">
            <BookOpen size={40} strokeWidth={1} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <span className="px-2 py-0.5 text-[9px] tracking-[0.28em] uppercase bg-black/70 text-gold border border-gold/40">
            {lang}
          </span>
          {totalGenerated > 0 && (
            <span className="px-2 py-0.5 text-[9px] tracking-[0.28em] uppercase bg-black/70 text-bone/80 border border-white/10">
              {totalGenerated} {t("pubstudio.card.posts")}
            </span>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1 gap-3">
        <h3 className="font-serif text-lg text-bone leading-snug line-clamp-2">{title}</h3>
        <div className="text-[10px] tracking-[0.22em] uppercase text-bone/45 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><Calendar size={10} strokeWidth={1.5} />{formatDate(article.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock size={10} strokeWidth={1.5} />{article.reading_time_min || 0} {t("pubstudio.card.min")}</span>
        </div>
        <div className="text-[10px] tracking-[0.14em] font-mono text-bone/40 truncate" title={article.public_url}>
          {article.public_url}
        </div>

        <div className="mt-auto pt-4 flex flex-wrap items-center gap-2 border-t border-white/5">
          <a
            href={article.public_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`pubstudio-open-${article.slug}`}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-3 py-2 border border-white/10 text-bone/80 hover:text-gold hover:border-gold/50 transition-colors"
          >
            <ExternalLink size={11} strokeWidth={1.5} />
            {t("pubstudio.card.open")}
          </a>
          <CopyButton
            text={article.public_url}
            size="sm"
            testId={`pubstudio-copyurl-${article.slug}`}
          />
          <button
            type="button"
            onClick={() => onOpen(article)}
            data-testid={`pubstudio-generate-${article.slug}`}
            className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase px-3 py-2 bg-gold text-ink hover:bg-gold-hover transition-colors"
          >
            <Sparkles size={11} strokeWidth={1.75} />
            {t("pubstudio.card.generate")}
          </button>
        </div>
      </div>
    </article>
  );
}

function JournalTab({ articles, loading, onOpen, t }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-bone/50 text-sm" data-testid="pubstudio-journal-loading">
        <Loader2 size={16} strokeWidth={1.5} className="animate-spin mr-3" />
        {t("pubstudio.loading")}
      </div>
    );
  }
  if (!articles.length) {
    return (
      <div className="text-center py-24 border border-white/5" data-testid="pubstudio-journal-empty">
        <BookOpen size={28} strokeWidth={1.2} className="mx-auto text-bone/30" />
        <p className="mt-4 font-serif text-xl text-bone/80">{t("pubstudio.empty.title")}</p>
        <p className="mt-2 text-sm text-bone/50 max-w-md mx-auto">{t("pubstudio.empty.body")}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="pubstudio-articles-grid">
      {articles.map((a) => (
        <ArticleCard key={a.id} article={a} onOpen={onOpen} t={t} />
      ))}
    </div>
  );
}

// ------------------------- Social tab (article -> platforms) -------------------------

function SocialStudio({ article, onBack, t }) {
  const [platform, setPlatform] = useState("linkedin");
  const [history, setHistory] = useState({}); // { platform: latestDoc }
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");
  const [imgStatus, setImgStatus] = useState(null); // null = checking
  const [fmtBusy, setFmtBusy] = useState(false);
  const [fmtCopied, setFmtCopied] = useState("");
  const [fmtErr, setFmtErr] = useState("");
  const fmtCacheRef = useRef(null);

  const copyFormat = async (fmt) => {
    setFmtBusy(true); setFmtErr("");
    try {
      if (!fmtCacheRef.current) fmtCacheRef.current = await adminApi.pubStudioFormats(article.id);
      const ok = await copyToClipboard(fmtCacheRef.current[fmt] || "");
      if (ok) { setFmtCopied(fmt); setTimeout(() => setFmtCopied(""), 1600); }
    } catch (e) { setFmtErr(e.message); }
    finally { setFmtBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    adminApi.pubStudioLinkedInPreview(article.id)
      .then((s) => { if (alive) setImgStatus(s); })
      .catch(() => { if (alive) setImgStatus({ ready: false, reason: "check_failed" }); });
    return () => { alive = false; };
  }, [article.id]);

  const loadHistory = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await adminApi.pubStudioHistory(article.id);
      const items = res.items || [];
      const map = {};
      for (const doc of items) {
        // First hit per platform is the newest thanks to backend sort.
        if (!map[doc.platform]) map[doc.platform] = doc;
      }
      setHistory(map);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [article.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async () => {
    setGenerating(true); setErr("");
    try {
      const doc = await adminApi.pubStudioGenerate(article.id, platform);
      setHistory((h) => ({ ...h, [platform]: doc }));
    } catch (e) {
      setErr(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const current = history[platform];
  const title = pickTitle(article, article.primary_language || "en");

  return (
    <div data-testid="pubstudio-social-studio">
      <button
        type="button"
        onClick={onBack}
        data-testid="pubstudio-social-back"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold transition-colors mb-8"
      >
        <ArrowLeft size={12} strokeWidth={1.5} /> {t("pubstudio.back")}
      </button>

      {/* Article header */}
      <div className="mb-10 pb-8 border-b border-white/5">
        <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">{t("pubstudio.social.forArticle")}</p>
        <h2 className="mt-3 font-serif text-2xl md:text-3xl text-bone leading-snug max-w-3xl">{title}</h2>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[10px] tracking-[0.22em] uppercase text-bone/50">
          <span>{formatDate(article.published_at)}</span>
          <span>{article.reading_time_min || 0} {t("pubstudio.card.min")}</span>
          <span className="font-mono normal-case tracking-[0.14em]">{article.public_url}</span>
        </div>

        {/* LinkedIn preview image status — green only when the server-side
            og:image of the article URL (= the AutoSEO cover) is verified. */}
        <div className="mt-6 flex items-center gap-4" data-testid="pubstudio-linkedin-image-status">
          {imgStatus?.ready && imgStatus?.cover && (
            <img
              src={imgStatus.cover}
              alt=""
              data-testid="pubstudio-linkedin-image-thumb"
              className="w-24 h-14 object-cover border border-gold/30"
              loading="lazy"
            />
          )}
          {imgStatus === null ? (
            <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-bone/50">
              <Loader2 size={11} strokeWidth={1.5} className="animate-spin" />
              {t("pubstudio.linkedin.imageChecking")}
            </span>
          ) : imgStatus.ready ? (
            <span
              data-testid="pubstudio-linkedin-image-ready"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 text-[10px] tracking-[0.22em] uppercase"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden />
              {t("pubstudio.linkedin.imageReady")}
              <span className="normal-case tracking-normal text-emerald-400/70">
                {imgStatus.width}×{imgStatus.height} · {imgStatus.mime}
              </span>
            </span>
          ) : (
            <span
              data-testid="pubstudio-linkedin-image-missing"
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-400 text-[10px] tracking-[0.22em] uppercase"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" aria-hidden />
              {t("pubstudio.linkedin.noImage")}
            </span>
          )}
        </div>
      </div>

      {/* Platform tabs */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="pubstudio-platform-tabs">
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const active = platform === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlatform(p.id)}
              data-testid={`pubstudio-platform-${p.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase transition-colors border ${
                active
                  ? "bg-gold text-ink border-gold"
                  : "border-white/10 text-bone/70 hover:text-bone hover:border-white/30"
              }`}
            >
              <Icon size={14} strokeWidth={1.5} className={active ? "" : p.accent} />
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Copy the article itself in ready-to-paste formats */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="pubstudio-formats">
        <span className="text-[10px] tracking-[0.22em] uppercase text-bone/40 mr-1">Copy article as</span>
        {["markdown", "html", "plain"].map((fmt) => (
          <button
            key={fmt}
            type="button"
            disabled={fmtBusy}
            onClick={() => copyFormat(fmt)}
            data-testid={`pubstudio-copy-format-${fmt}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase border border-white/15 text-bone/60 hover:border-gold/40 hover:text-gold disabled:opacity-40 transition-colors"
          >
            {fmtCopied === fmt ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.5} />}
            {fmt === "plain" ? "Plain text" : fmt === "html" ? "HTML" : "Markdown"}
          </button>
        ))}
        {fmtErr && <span className="text-[11px] text-red-400">{fmtErr}</span>}
      </div>

      {/* Preview + actions */}
      <div className="border border-white/8 bg-[#080706]" data-testid={`pubstudio-preview-${platform}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">
              {current ? t("pubstudio.social.preview") : t("pubstudio.social.notGenerated")}
            </p>
            {current?.generated_at && (
              <p className="mt-1 text-[10px] text-bone/40">
                {t("pubstudio.social.lastGenerated")}: {formatDate(current.generated_at)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {current?.content && (
              <CopyButton
                text={current.content}
                testId={`pubstudio-copy-${platform}`}
              />
            )}
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              data-testid={`pubstudio-regenerate-${platform}`}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase px-4 py-2.5 bg-gold text-ink hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? <Loader2 size={12} strokeWidth={2} className="animate-spin" /> : (current ? <RefreshCw size={12} strokeWidth={1.75} /> : <Sparkles size={12} strokeWidth={1.75} />)}
              {generating ? t("pubstudio.generating") : (current ? t("pubstudio.regenerate") : t("pubstudio.generate"))}
            </button>
          </div>
        </div>

        <div className="p-6 md:p-10 min-h-[280px]">
          {loading && !current ? (
            <div className="flex items-center gap-3 text-bone/50 text-sm">
              <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
              {t("pubstudio.loading")}
            </div>
          ) : current?.content ? (
            <pre
              data-testid={`pubstudio-preview-text-${platform}`}
              className="font-serif text-[15px] md:text-base text-bone/90 leading-[1.75] whitespace-pre-wrap break-words"
              style={{ fontFamily: '"Crimson Text", Georgia, serif' }}
            >
              {current.content}
            </pre>
          ) : (
            <div className="text-center py-16">
              <Sparkles size={22} strokeWidth={1.2} className="mx-auto text-bone/25" />
              <p className="mt-4 text-bone/60 text-sm">{t("pubstudio.social.emptyBody")}</p>
              <p className="mt-2 text-[11px] tracking-[0.2em] uppercase text-bone/35">
                {t("pubstudio.social.emptyHint")}
              </p>
            </div>
          )}
          {err && (
            <p className="mt-6 text-sm text-red-400" data-testid={`pubstudio-error-${platform}`}>
              {err}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------- Newsletter tab -------------------------

function NewsletterTab({ articles, t }) {
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generate = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setGenerating(true); setErr(""); setResult(null);
    try {
      const doc = await adminApi.pubStudioNewsletter(ids);
      setResult(doc);
    } catch (e) {
      setErr(e.message || "Newsletter generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const sections = result?.sections || {};
  const fullText = result?.content || "";

  return (
    <div data-testid="pubstudio-newsletter">
      <div className="mb-8 max-w-2xl">
        <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">{t("pubstudio.newsletter.eyebrow")}</p>
        <h2 className="mt-3 font-serif text-2xl md:text-3xl text-bone leading-snug">{t("pubstudio.newsletter.title")}</h2>
        <p className="mt-3 text-sm text-bone/60 leading-relaxed">{t("pubstudio.newsletter.body")}</p>
      </div>

      {/* Article picker */}
      <div className="border border-white/8 bg-[#080706] mb-8">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <p className="text-[10px] tracking-[0.28em] uppercase text-bone/70">
            {t("pubstudio.newsletter.pick")} · {selected.size} {t("pubstudio.newsletter.selected")}
          </p>
          <button
            type="button"
            onClick={generate}
            disabled={!selected.size || generating}
            data-testid="pubstudio-newsletter-generate"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase px-4 py-2.5 bg-gold text-ink hover:bg-gold-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? <Loader2 size={12} strokeWidth={2} className="animate-spin" /> : <Sparkles size={12} strokeWidth={1.75} />}
            {generating ? t("pubstudio.generating") : t("pubstudio.newsletter.generate")}
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {articles.length === 0 && (
            <p className="p-6 text-sm text-bone/50">{t("pubstudio.empty.title")}</p>
          )}
          {articles.map((a) => {
            const isSel = selected.has(a.id);
            return (
              <label
                key={a.id}
                data-testid={`pubstudio-newsletter-pick-${a.slug}`}
                className={`flex items-center gap-4 px-6 py-3 border-b border-white/5 last:border-b-0 cursor-pointer transition-colors ${
                  isSel ? "bg-gold/5" : "hover:bg-white/[0.02]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(a.id)}
                  className="accent-gold w-4 h-4"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[15px] text-bone truncate">{pickTitle(a, a.primary_language)}</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-bone/45 mt-0.5">
                    {formatDate(a.published_at)} · {a.reading_time_min || 0} {t("pubstudio.card.min")}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {err && <p className="mb-6 text-sm text-red-400" data-testid="pubstudio-newsletter-error">{err}</p>}

      {result && (
        <div className="border border-white/8 bg-[#080706]" data-testid="pubstudio-newsletter-result">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
            <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">
              {t("pubstudio.newsletter.digest")}
            </p>
            <CopyButton
              text={fullText}
              testId="pubstudio-newsletter-copyall"
            />
          </div>
          <div className="p-6 md:p-10 space-y-8">
            {[
              { key: "subject",              labelKey: "pubstudio.newsletter.field.subject" },
              { key: "introduction",         labelKey: "pubstudio.newsletter.field.intro" },
              { key: "main_article",         labelKey: "pubstudio.newsletter.field.main" },
              { key: "closing_reflection",   labelKey: "pubstudio.newsletter.field.closing" },
              { key: "cta",                  labelKey: "pubstudio.newsletter.field.cta" },
            ].map((f) => {
              const value = sections[f.key] || "";
              if (!value) return null;
              return (
                <section key={f.key} className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 md:gap-8">
                  <div className="flex items-start justify-between md:justify-start md:flex-col gap-2">
                    <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">{t(f.labelKey)}</p>
                    <CopyButton text={value} size="sm" testId={`pubstudio-newsletter-copy-${f.key}`} />
                  </div>
                  <pre
                    className="font-serif text-[15px] text-bone/90 leading-[1.75] whitespace-pre-wrap break-words"
                    style={{ fontFamily: '"Crimson Text", Georgia, serif' }}
                    data-testid={`pubstudio-newsletter-text-${f.key}`}
                  >
                    {value}
                  </pre>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------- Analytics tab -------------------------

function AnalyticsTab({ t }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi.pubStudioAnalytics()
      .then((d) => { if (!cancelled) setSnapshot(d); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const per = snapshot?.generated_per_platform || {};
  const most = snapshot?.most_generated_articles || [];

  return (
    <div data-testid="pubstudio-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard
          testId="pubstudio-stat-articles-published"
          icon={BookOpen}
          label={t("pubstudio.analytics.articlesPublished")}
          value={snapshot?.articles_published ?? "—"}
          loading={loading}
        />
        <StatCard
          testId="pubstudio-stat-linkedin"
          icon={Linkedin}
          label={t("pubstudio.analytics.linkedinPosts")}
          value={per.linkedin ?? 0}
          loading={loading}
        />
        <StatCard
          testId="pubstudio-stat-substack"
          icon={FileText}
          label={t("pubstudio.analytics.substackPosts")}
          value={per.substack ?? 0}
          loading={loading}
        />
        <StatCard
          testId="pubstudio-stat-generated-total"
          icon={Sparkles}
          label={t("pubstudio.analytics.totalGenerated")}
          value={snapshot?.generated_total ?? 0}
          loading={loading}
        />
      </div>

      {/* Live counters strip */}
      <div className="mb-10 border border-white/8 bg-[#080706]" data-testid="pubstudio-per-platform">
        <div className="px-6 py-4 border-b border-white/5">
          <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">
            {t("pubstudio.analytics.byPlatform")}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5">
          {["linkedin", "x", "threads", "substack", "newsletter"].map((p) => (
            <div key={p} className="px-6 py-6 border-r border-white/5 last:border-r-0 border-b md:border-b-0">
              <p className="text-[10px] tracking-[0.28em] uppercase text-bone/50">{p}</p>
              <p className="mt-2 font-serif text-3xl text-bone">{per[p] ?? 0}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Most-generated articles (proxy until GA) */}
      <div className="mb-10 border border-white/8 bg-[#080706]" data-testid="pubstudio-most-generated">
        <div className="px-6 py-4 border-b border-white/5">
          <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70">
            {t("pubstudio.analytics.mostGenerated")}
          </p>
        </div>
        {most.length === 0 ? (
          <p className="p-6 text-sm text-bone/50">{t("pubstudio.analytics.noData")}</p>
        ) : (
          <ul>
            {most.map((row) => (
              <li key={row.article_id} className="px-6 py-4 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-serif text-[15px] text-bone truncate">{row.title || row.slug}</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-bone/45 mt-1 font-mono normal-case tracking-[0.14em]">
                    https://stoicos.ai/stoic-journal/{row.slug}
                  </p>
                </div>
                <span className="text-[11px] tracking-[0.24em] uppercase text-gold flex-shrink-0">
                  {row.count} {t("pubstudio.analytics.posts")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Placeholder tiles for future integrations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pubstudio-placeholder-tiles">
        {[
          { icon: Globe, key: "trafficSources" },
          { icon: Clock, key: "readingTime" },
          { icon: TrendingUp, key: "ctrVirtueGuide" },
        ].map((tile) => {
          const Icon = tile.icon;
          return (
            <div
              key={tile.key}
              className="border border-white/8 bg-[#080706] p-6"
              data-testid={`pubstudio-tile-${tile.key}`}
            >
              <Icon size={20} strokeWidth={1.4} className="text-gold/70" />
              <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-bone/60">
                {t(`pubstudio.analytics.${tile.key}`)}
              </p>
              <p className="mt-2 font-serif text-2xl text-bone/40">—</p>
              <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-bone/35">
                {t("pubstudio.analytics.pendingIntegration")}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------- Page shell -------------------------

export default function PublishingStudio() {
  const { t } = useT();
  const [tab, setTab] = useState("journal");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminApi.pubStudioListArticles()
      .then((d) => { if (!cancelled) setArticles(d.articles || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openSocial = (article) => {
    setSelectedArticle(article);
    setTab("social");
  };

  const intro = useMemo(() => {
    if (tab === "journal")    return t("pubstudio.intro.journal");
    if (tab === "social")     return t("pubstudio.intro.social");
    if (tab === "newsletter") return t("pubstudio.intro.newsletter");
    return t("pubstudio.intro.analytics");
  }, [tab, t]);

  return (
    <AdminShell title={t("pubstudio.title")}>
      <p className="text-sm text-bone/60 -mt-6 mb-8 max-w-2xl">{intro}</p>

      <div className="border-b border-white/8 mb-10 flex flex-wrap" data-testid="pubstudio-tabs">
        {TABS.map((tb) => (
          <TabButton
            key={tb.id}
            tab={tb}
            active={tab === tb.id}
            onClick={() => setTab(tb.id)}
            t={t}
          />
        ))}
      </div>

      {tab === "journal"    && <JournalTab   articles={articles} loading={loading} onOpen={openSocial} t={t} />}
      {tab === "social"     && (
        selectedArticle
          ? <SocialStudio article={selectedArticle} onBack={() => setTab("journal")} t={t} />
          : <SocialPickPrompt articles={articles} onPick={openSocial} t={t} />
      )}
      {tab === "newsletter" && <NewsletterTab articles={articles} t={t} />}
      {tab === "analytics"  && <AnalyticsTab  t={t} />}
    </AdminShell>
  );
}

function SocialPickPrompt({ articles, onPick, t }) {
  return (
    <div className="text-center py-20 border border-dashed border-white/10" data-testid="pubstudio-social-pick">
      <Share2 size={26} strokeWidth={1.2} className="mx-auto text-bone/40" />
      <p className="mt-4 font-serif text-xl text-bone/85">{t("pubstudio.social.pickTitle")}</p>
      <p className="mt-2 text-sm text-bone/50 max-w-md mx-auto">{t("pubstudio.social.pickBody")}</p>
      {articles.length > 0 && (
        <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
          {articles.slice(0, 8).map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onPick(a)}
              data-testid={`pubstudio-social-pick-${a.slug}`}
              className="text-[10px] tracking-[0.22em] uppercase px-4 py-2 border border-white/10 text-bone/75 hover:text-gold hover:border-gold/50 transition-colors truncate max-w-[280px]"
              title={pickTitle(a, a.primary_language)}
            >
              {pickTitle(a, a.primary_language)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
